// Conference control client. Always talks DIRECTLY to the calling service
// (rtc.getnotifi.com) — the same host the softphone already fetches its SIP
// credentials from — so no operations proxy needs to be deployed. The feature
// is ON by default on the service and self-provisions its Telnyx conference
// connection; caller-ID comes from each tenant's own DID. Set
// CONFERENCE_DISABLED=true on the service to turn it off.

import { fetchAllUsers } from '@/hooks/settings/useUsers';
import { useAuthStore } from '@/stores/auth.store';
import { getActiveTenantId } from '@/lib/activeTenant';
import type { ConferenceApi, ConferenceState } from './types';
import type {
  ConferenceLogResponse,
  ConferenceLogsResponse,
  ListConferenceLogsParams,
} from './callLogTypes';

// Calling service base — same resolution as the softphone's callingService.ts:
// unset ⇒ prod rtc host; explicit value wins; '' disables. Uses ?? so '' stays ''.
const CALLING_SERVICE_URL = (
  (import.meta.env.VITE_CALLING_SERVICE_URL as string | undefined) ?? 'https://rtc.getnotifi.com'
).replace(/\/+$/, '');

// Conference CONTROL API on the calling service. The browser is already an
// authenticated client of this host (it POSTs /api/devices/sip/credentials with
// the notifi ES256 JWT + x-tenant-id to register), so the same Bearer + tenant
// header pair authorizes these routes — the service verifies against our JWKS
// and owner-scopes every conference to the caller.
const P = '/api/conference';

/**
 * False when `VITE_CALLING_SERVICE_URL` is set to the empty string — the same
 * switch that leaves the softphone unregistered (`isCallingConfigured`). Without
 * this the base is '' and every request would resolve against the app's own
 * origin, which serves no `/api/conference`; the conference would appear
 * available and fail on use in a deployment where calling is off on purpose.
 */
export const isConferenceConfigured = CALLING_SERVICE_URL !== '';

/**
 * Call the calling service directly with the caller's JWT + active tenant. The
 * service wraps replies as `{ success, ... }`; a non-2xx surfaces the service's
 * `message` (e.g. the 503 kill-switch or a 409 "no active SIP credential").
 */
async function rtcFetch<T>(method: string, path: string, body?: unknown): Promise<T> {
  if (!isConferenceConfigured) {
    throw new Error('Calling is not configured for this environment');
  }
  const token = useAuthStore.getState().token;
  const tenantId = getActiveTenantId();
  if (!token) throw new Error('Not authenticated — no token for conference');
  const res = await fetch(`${CALLING_SERVICE_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await res.text();
  let json: unknown = {};
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { message: text };
  }
  if (!res.ok) {
    const msg =
      (json as { message?: string })?.message || `Conference request failed (${res.status})`;
    throw new Error(msg);
  }
  return json as T;
}

const httpConferenceApi: ConferenceApi = {
  // Not `POST /api/conference`, which has the service dial the host's own
  // devices first. That leg is refused at Telnyx for browser callers ("Failed to
  // start the conference"), and it needed a window where this tab auto-answered
  // any inbound call. Dialling in is the flow the mobile app uses: the service
  // prepares the room, we place an ordinary outbound call into it, and it dials
  // everyone else once our leg arrives.
  startInternalCall: async (targetUserIds, hostName) => {
    const r = await rtcFetch<{ conferenceId?: string; dialTarget?: string; callerId?: string }>(
      'POST',
      `${P}/internal-call`,
      {
        targetUserIds,
        ...(hostName?.trim() ? { hostName: hostName.trim() } : {}),
      },
    );
    // Both or nothing: a room we cannot dial into is a room nobody can enter.
    if (!r.conferenceId || !r.dialTarget) {
      throw new Error('The calling service did not return a room to join');
    }
    return {
      conferenceId: r.conferenceId,
      dialTarget: r.dialTarget,
      callerId: r.callerId ?? null,
    };
  },
  getState: async (id) => {
    const r = await rtcFetch<{
      conference: ConferenceState['conference'];
      participants: ConferenceState['participants'];
    }>('GET', `${P}/${id}`);
    return { conference: r.conference, participants: r.participants };
  },
  addParticipant: async (id, targetUserId) => {
    await rtcFetch('POST', `${P}/${id}/participants`, { targetUserId });
  },
  addPstn: async (id, e164) => {
    await rtcFetch('POST', `${P}/${id}/pstn`, { e164 });
  },
  setMuted: async (id, ccid, muted) => {
    await rtcFetch('POST', `${P}/${id}/participants/${ccid}/${muted ? 'mute' : 'unmute'}`, {});
  },
  setHold: async (id, ccid, onHold) => {
    await rtcFetch('POST', `${P}/${id}/participants/${ccid}/${onHold ? 'hold' : 'resume'}`, {});
  },
  remove: async (id, ccid) => {
    await rtcFetch('DELETE', `${P}/${id}/participants/${ccid}`);
  },
  end: async (id) => {
    await rtcFetch('POST', `${P}/${id}/end`, {});
  },
  // Team members come from operations (the CRM owns the user directory),
  // regardless of mock/real conference control.
  roster: async () => {
    // EVERY page, via the Settings users table's own helper: `GET /users` pages
    // at 20 by default, and reading only the first page left everyone past the
    // 20th member (by email) out of the invite list. It works from `totalPages`
    // and fails outright if a page fails, so the list is never quietly partial.
    const { data: list } = await fetchAllUsers();
    const seen = new Set<string>();
    return (
      list
        // A member who moves between pages while they load (someone added mid-fetch
        // shifts the email order) can come back twice; keep the first.
        .filter((u) => u && u.id && !seen.has(u.id) && seen.add(u.id))
        .map((u) => ({
          userId: u.id,
          name:
            u.displayName?.trim() ||
            [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
            u.email ||
            u.id,
        }))
    );
  },
};

export const conferenceApi: ConferenceApi = httpConferenceApi;

/**
 * Conference call HISTORY — derived on the calling service from the Conference /
 * participant records (a conference is multi-party, so it lives outside the 1:1
 * CallLog feed). Same direct-to-rtc transport as the control API above, so it
 * is gated by the same `isConferenceConfigured` check inside `rtcFetch`.
 */
export const conferenceLogsApi = {
  list: (params: ListConferenceLogsParams = {}) => {
    const qs = new URLSearchParams();
    if (params.limit != null) qs.set('limit', String(params.limit));
    if (params.skip != null) qs.set('skip', String(params.skip));
    if (params.status) qs.set('status', params.status);
    const q = qs.toString();
    return rtcFetch<ConferenceLogsResponse>('GET', `${P}/call-logs${q ? `?${q}` : ''}`);
  },
  detail: (conferenceId: string) =>
    rtcFetch<ConferenceLogResponse>('GET', `${P}/call-logs/${encodeURIComponent(conferenceId)}`),
};
